import { computed } from 'vue'
import type { BaseWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'

function isConstrainedWidth(value?: string): boolean {
  if (!value || value.trim() === '') return false
  const normalized = value.trim().toLowerCase()
  return normalized !== 'auto' && normalized !== 'none' && normalized !== '100%'
}

/** Center block when width/maxWidth is narrower than the container. */
export function shouldCenterWidgetBlock(widget: BaseWidget): boolean {
  if (widget.marginLeft !== undefined || widget.marginRight !== undefined) return false
  return isConstrainedWidth(widget.width) || isConstrainedWidth(widget.maxWidth)
}

export function useWidgetStyles(widget: BaseWidget) {
  const widgetStyles = computed(() => {
    const styles: Record<string, string> = {}
    
    // Layout & Dimensions
    if (widget.width) styles.width = widget.width
    if (widget.maxWidth) styles.maxWidth = widget.maxWidth
    if (widget.height) styles.height = widget.height
    if (widget.minHeight) styles.minHeight = widget.minHeight
    
    // Spacing
    if (widget.padding) {
      styles.padding = widget.padding
    } else {
      // Individual padding values
      if (widget.paddingTop) styles.paddingTop = widget.paddingTop
      if (widget.paddingRight) styles.paddingRight = widget.paddingRight
      if (widget.paddingBottom) styles.paddingBottom = widget.paddingBottom
      if (widget.paddingLeft) styles.paddingLeft = widget.paddingLeft
    }
    
    if (widget.margin) {
      styles.margin = widget.margin
    } else {
      // Individual margin values
      if (widget.marginTop) styles.marginTop = widget.marginTop
      if (widget.marginRight) styles.marginRight = widget.marginRight
      if (widget.marginBottom) styles.marginBottom = widget.marginBottom
      if (widget.marginLeft) styles.marginLeft = widget.marginLeft
    }

    if (shouldCenterWidgetBlock(widget)) {
      styles.marginLeft = 'auto'
      styles.marginRight = 'auto'
    }
    
    // Background (solid or gradient from Style tab)
    Object.assign(styles, resolveWidgetBackgroundStyle(widget))
    if (widget.backgroundImage) styles.backgroundImage = widget.backgroundImage
    if (widget.backgroundSize) styles.backgroundSize = widget.backgroundSize
    if (widget.backgroundPosition) styles.backgroundPosition = widget.backgroundPosition
    if (widget.backgroundRepeat) styles.backgroundRepeat = widget.backgroundRepeat
    if (widget.backgroundAttachment) styles.backgroundAttachment = widget.backgroundAttachment
    
    // Border
    if (widget.borderStyle && widget.borderStyle !== 'none') {
      styles.borderStyle = widget.borderStyle
      if (widget.borderWidth) styles.borderWidth = widget.borderWidth
      if (widget.borderColor) styles.borderColor = widget.borderColor
    }
    
    // Border Radius
    if (widget.borderRadius) {
      styles.borderRadius = widget.borderRadius
    } else {
      // Individual border radius values
      if (widget.borderTopLeftRadius) styles.borderTopLeftRadius = widget.borderTopLeftRadius
      if (widget.borderTopRightRadius) styles.borderTopRightRadius = widget.borderTopRightRadius
      if (widget.borderBottomRightRadius) styles.borderBottomRightRadius = widget.borderBottomRightRadius
      if (widget.borderBottomLeftRadius) styles.borderBottomLeftRadius = widget.borderBottomLeftRadius
    }
    
    // Box Shadow
    if (widget.boxShadow) {
      styles.boxShadow = widget.boxShadow
    } else if (widget.boxShadowHorizontal || widget.boxShadowVertical) {
      // Build box-shadow from individual values
      const h = widget.boxShadowHorizontal || '0px'
      const v = widget.boxShadowVertical || '0px'
      const b = widget.boxShadowBlur || '10px'
      const s = widget.boxShadowSpread || '0px'
      const c = widget.boxShadowColor || 'rgba(0,0,0,0.1)'
      const p = widget.boxShadowPosition === 'inset' ? 'inset' : ''
      styles.boxShadow = `${p} ${h} ${v} ${b} ${s} ${c}`.trim()
    }
    
    // Typography
    if (widget.fontFamily) styles.fontFamily = widget.fontFamily
    if (widget.fontSize) styles.fontSize = widget.fontSize
    if (widget.fontWeight) styles.fontWeight = widget.fontWeight
    if (widget.fontStyle) styles.fontStyle = widget.fontStyle
    if (widget.textTransform) styles.textTransform = widget.textTransform
    if (widget.textDecoration) styles.textDecoration = widget.textDecoration
    if (widget.lineHeight) styles.lineHeight = widget.lineHeight
    if (widget.letterSpacing) styles.letterSpacing = widget.letterSpacing
    if (widget.wordSpacing) styles.wordSpacing = widget.wordSpacing
    if (widget.textAlign) styles.textAlign = widget.textAlign
    if (widget.textColor) styles.color = widget.textColor
    
    // Transform
    const transforms: string[] = []
    if (widget.rotate) transforms.push(`rotate(${widget.rotate})`)
    if (widget.scaleX || widget.scaleY) {
      const sx = widget.scaleX || '1'
      const sy = widget.scaleY || widget.scaleX || '1'
      transforms.push(`scale(${sx}, ${sy})`)
    }
    if (widget.translateX || widget.translateY) {
      const tx = widget.translateX || '0px'
      const ty = widget.translateY || '0px'
      transforms.push(`translate(${tx}, ${ty})`)
    }
    if (widget.skewX) transforms.push(`skewX(${widget.skewX})`)
    if (widget.skewY) transforms.push(`skewY(${widget.skewY})`)
    
    if (widget.transform) {
      styles.transform = widget.transform
    } else if (transforms.length > 0) {
      styles.transform = transforms.join(' ')
    }
    
    if (widget.transformOrigin) styles.transformOrigin = widget.transformOrigin
    
    // Filters & Effects
    const filters: string[] = []
    if (widget.blur) filters.push(`blur(${widget.blur})`)
    if (widget.brightness) filters.push(`brightness(${widget.brightness})`)
    if (widget.contrast) filters.push(`contrast(${widget.contrast})`)
    if (widget.saturate) filters.push(`saturate(${widget.saturate})`)
    if (widget.hueRotate) filters.push(`hue-rotate(${widget.hueRotate})`)
    if (widget.grayscale) filters.push(`grayscale(${widget.grayscale})`)
    
    if (filters.length > 0) {
      styles.filter = filters.join(' ')
    }
    
    if (widget.opacity) styles.opacity = widget.opacity
    
    // Hover Effects & Transitions
    if (widget.transitionDuration) {
      const timing = widget.transitionTimingFunction || 'ease'
      styles.transition = `all ${widget.transitionDuration} ${timing}`
    }
    
    // Position & Display
    if (widget.position) styles.position = widget.position
    if (widget.top) styles.top = widget.top
    if (widget.right) styles.right = widget.right
    if (widget.bottom) styles.bottom = widget.bottom
    if (widget.left) styles.left = widget.left
    if (widget.zIndex) styles.zIndex = widget.zIndex
    if (widget.display) styles.display = widget.display
    
    // Flexbox
    if (widget.flexDirection) styles.flexDirection = widget.flexDirection
    if (widget.justifyContent) styles.justifyContent = widget.justifyContent
    if (widget.alignItems) styles.alignItems = widget.alignItems
    if (widget.gap) styles.gap = widget.gap
    
    // Animation
    if (widget.entranceAnimation && widget.entranceAnimation !== 'none') {
      const duration = widget.animationDuration || '0.5s'
      const delay = widget.animationDelay || '0s'
      const iterations = widget.animationIterationCount || '1'
      styles.animation = `${widget.entranceAnimation} ${duration} ease-out ${delay} ${iterations} both`
    }
    
    // Custom CSS (raw CSS string)
    if (widget.customCSS) {
      // Parse custom CSS and merge with styles
      // Note: This is a simple implementation. For complex CSS, consider using a style tag
      const customStyles = widget.customCSS.split(';')
      customStyles.forEach(rule => {
        const [property, value] = rule.split(':').map(s => s.trim())
        if (property && value) {
          // Convert kebab-case to camelCase
          const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
          styles[camelProperty] = value
        }
      })
    }
    
    return styles
  })
  
  const widgetClasses = computed(() => {
    const classes: string[] = []
    
    // Custom Class
    if (widget.customClass) {
      classes.push(widget.customClass)
    }
    
    // Animation Class
    if (widget.entranceAnimation && widget.entranceAnimation !== 'none') {
      classes.push('widget-animated')
    }
    
    // Responsive classes
    if (widget.hidden_on_mobile) classes.push('d-none d-md-block')
    if (widget.hidden_on_tablet) classes.push('d-md-none d-lg-block')
    if (widget.hidden_on_desktop) classes.push('d-lg-none')
    
    return classes.join(' ')
  })
  
  // Hover styles (to be applied via CSS variables or inline event handlers)
  const hoverStyles = computed(() => {
    const styles: Record<string, string> = {}
    
    if (widget.hoverTransform) styles.transform = widget.hoverTransform
    if (widget.hoverBoxShadow) styles.boxShadow = widget.hoverBoxShadow
    if (widget.hoverBorderColor) styles.borderColor = widget.hoverBorderColor
    if (widget.hoverBackgroundColor) styles.backgroundColor = widget.hoverBackgroundColor
    if (widget.hoverTextColor) styles.color = widget.hoverTextColor
    if (widget.hoverOpacity) styles.opacity = widget.hoverOpacity
    
    return styles
  })
  
  return {
    widgetStyles,
    widgetClasses,
    hoverStyles
  }
}
